import { captureException } from '@sentry/react'
import { useEffect } from 'react'
import { useOutletContext } from 'react-router'
import { Container } from '@/components/ui/container'
import { HostReviewWaiting } from '@/features/host-review/components/host-review-waiting'
import { UploaderFolderViewPanel } from '@/features/host-review/components/uploader-folder-view'
import { useGetPhotosetReviewState } from '@/features/host-review/queries/use-get-photoset-review-state'
import { useGetUploaderFolderView } from '@/features/host-review/queries/use-get-uploader-folder-view'
import { PhotosetCompletedDownload } from '@/features/photoset/components/photoset-completed-download'
import type { PhotosetURLParams } from '@/features/photoset/types'

/**
 * The screen at the end of post-production.
 *
 * Which of three things renders is decided by the review state the backend
 * derives per photoset, never by a feature flag: during the gradual rollout two
 * shoots side by side legitimately traverse different step sequences.
 *
 * - no review (control cohort, pre-launch, or feature off) - today's page,
 *   unchanged;
 * - review open - the waiting page, with no operator action;
 * - review closed - the kept/removed folder view.
 */
export const PhotosetCompletedSteps = () => {
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const photosetId = Number(urlParams.id)

	// A review ends when the host acts or the expiry sweep fires, and neither
	// tells this client, so the waiting page only moves on when the operator
	// reloads it.
	const {
		data: reviewState,
		isPending: isPendingReviewState,
		fetchStatus: reviewStateFetchStatus,
		error: reviewStateError
	} = useGetPhotosetReviewState(photosetId)

	// Only an actual in-flight request justifies the spinner. Offline, the query
	// client pauses rather than fails (`networkMode: 'online'`), so a query that
	// is merely pending would spin forever with no error branch to escape to.
	const isLoadingReviewState =
		isPendingReviewState && reviewStateFetchStatus === 'fetching'

	// A failed request is not the same as "no review", but both fall through to
	// the legacy screen and look identical from the outside. Reporting it keeps a
	// broken endpoint from masquerading as a control-cohort photoset.
	useEffect(() => {
		if (reviewStateError) {
			captureException(reviewStateError)
		}
	}, [reviewStateError])

	const isPending = reviewState?.reviewState === 'pending_host_review'
	const isClosed = reviewState?.reviewState === 'review_closed'

	const {
		data: folderView,
		isPending: isPendingFolders,
		fetchStatus: foldersFetchStatus,
		error: folderViewError
	} = useGetUploaderFolderView(isClosed ? photosetId : undefined)

	// The folder view is a second round-trip that only starts once the review is
	// known to be closed. Without holding the screen for it, the fallback below
	// renders the legacy download page for a moment before the folder view
	// arrives and replaces it.
	const isLoadingFolders =
		isPendingFolders && foldersFetchStatus === 'fetching'

	useEffect(() => {
		if (folderViewError) {
			captureException(folderViewError)
		}
	}, [folderViewError])

	if (isLoadingReviewState || isLoadingFolders) {
		return (
			<Container
				className='flex flex-col items-center justify-center bg-neutral-50'
				fullWidth
			>
				<HostReviewWaiting isLoading />
			</Container>
		)
	}

	if (isPending) {
		return (
			<Container
				className='flex flex-col items-center justify-center bg-neutral-50'
				fullWidth
			>
				<HostReviewWaiting review={reviewState?.review} />
			</Container>
		)
	}

	if (isClosed && folderView) {
		return (
			<Container className='bg-neutral-50' fullWidth>
				<UploaderFolderViewPanel view={folderView} />
			</Container>
		)
	}

	// No review, or a closed review whose folder view never arrived: today's
	// page. A photoset that never entered a review must render exactly as it
	// does now, and a closed review we cannot describe is better served by the
	// legacy screen than by a spinner that never resolves.
	return (
		<Container
			className='flex flex-col items-center justify-center bg-neutral-50'
			fullWidth
		>
			<PhotosetCompletedDownload urlParams={urlParams} />
		</Container>
	)
}

PhotosetCompletedSteps.displayName = 'PhotosetCompletedSteps'
